import MobileNavBar from '@/components/MobileNavBar';
import { TrackList } from '@/components/TrackList';
import { useDimensions } from '@/hooks/useDimensions';
import { flattenNestedObject, shuffleArray } from '@/utils';
import { useEffect, useState } from 'react';
import DiscoverIcon from '@/assets/icons/discover.svg';
import LightingIcon from '@/assets/icons/lighting.svg';
import RightIcon from '@/assets/icons/right.svg';
import MarketplaceIcon from '@/assets/icons/marketplace.svg';

import PlayerBgUrl from '@/assets/images/home/player.png';
import { useLocation, useNavigate } from 'react-router-dom';

import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import SimpleEffects from '../Home/Generator/components/SimpleEffects';
import { useModel } from '../Home/Generator/model';
import { useGeneratorFilterStore } from '@/state/generator-filter-store';

const HomePage = () => {
  const { isMobileOrPad } = useDimensions();
  const { setCurrentFilterGroup } = useGeneratorFilterStore();
  const location = useLocation();

  useEffect(() => {
    setCurrentFilterGroup(null);
  }, [location]);
  const { form, onSubmit } = useModel();
  const navigate = useNavigate();

  return (
    <div className=" relative min-h-screen bg-jenmusic-background-primary lg:bg-[#f6f6f7]">
      {isMobileOrPad && <MobileNavBar />}
      <div className="py-6 px-3 lg:px-12 lg:py-8  max-w-[1440px] box-content mx-auto">
        <div className="grid lg:grid-cols-[1fr_354px] gap-[10px] min-h-[440px] mb-3">
          <div className="pt-8 rounded-xl bg-jenmusic-background-primary grid lg:grid-cols-[336px_1fr] overflow-hidden border border-b-jenmusic-border-primary">
            <IntroduceStyleFilter />

            <StyleFilterScrollArea />
          </div>
          <MakeMusic />
        </div>
        <Discover />
      </div>
      <div className=" sticky bottom-0 w-full">
        <form
          className="flex flex-col justify-center items-center"
          onSubmit={(event) => {
            event.preventDefault();
            form.handleSubmit(onSubmit as any)();
            navigate('/');
          }}
        >
          <SimpleEffects generatorLoading={false} />
        </form>
      </div>
    </div>
  );
};

const IntroduceStyleFilter = () => {
  const navigate = useNavigate();

  return (
    <div className=" h-full pl-8 pb-8 flex flex-col justify-between">
      <div className="flex text-jenmusic-content-primary typography-jenmusic-heading-medium gap-3">
        <MarketplaceIcon fillColor="#111112" /> <div>Marketplace</div>
      </div>
      <div className="mt-8 flex flex-col gap-3">
        <div className="  typography-jenmusic-heading-medium text-jenmusic-content-primary">
          Introducing StyleFilters<sup className="text-[8px] top-[-12px]">TM</sup>
        </div>
        <div className=" typography-jenmusic-base tracking-[-0.4px] text-jenmusic-content-secondary">
          Your sound. Their style. StyleFilter™ is our patented AI technology that lets you create your own music—“jenerations”—infused
          with the distinct style, vibe, feel, instrument textures, and structure of songs by your favorite artists and producers.
        </div>
      </div>
      <div className="flex gap-1 hover:opacity-80 cursor-pointer mt-8 items-center" onClick={() => navigate('/marketplace')}>
        <div className=" rounded-full bg-jenmusic-interactive-accent w-6 h-6 flex justify-center items-center ">
          <RightIcon className=" fill-jenmusic-interactive" />
        </div>
        <div className="text-jenmusic-interactive typography-jenmusic-base">
          Shop StyleFilters<sup className="text-[8px]">TM</sup>
        </div>
      </div>
    </div>
  );
};

const StyleFilterScrollArea = () => {
  return (
    <ScrollArea className="h-full ">
      <div className="h-[150px] overflow-y-hidden lg:h-full flex pl-4 pb-[25px] gap-3 lg:gap-6 overflow-auto min-w-0 ">
        {['imo-headlock', 'imo-lnoae', 'imo-gag', 'imo-jfn', 'imo-whydtm'].map((name) => {
          return (
            <div
              key={name}
              style={{
                background: `url(${require(`@/assets/images/home/${name}.png`)})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                boxShadow:
                  '75px 118px 39px 0px rgba(0, 0, 0, 0.00), 48px 75px 36px 0px rgba(0, 0, 0, 0.03), 27px 42px 30px 0px rgba(0, 0, 0, 0.10), 12px 19px 22px 0px rgba(0, 0, 0, 0.17), 3px 5px 12px 0px rgba(0, 0, 0, 0.20)',
              }}
              className="flex-shrink-0 w-[141px] lg:w-[235px] h-[229px] lg:h-[383px] bg-jenmusic-interactive rounded-[8px]"
            ></div>
          );
        })}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};

const MakeMusic = () => {
  const navigate = useNavigate();

  return (
    <div className="p-8 rounded-xl bg-jenmusic-background-primary flex flex-col justify-between border border-b-jenmusic-border-primary gap-12">
      <div className="flex flex-col gap-4">
        <div className="flex gap-3">
          <LightingIcon fillColor="#111112" />
          <div className="  typography-jenmusic-heading-medium text-jenmusic-content-primary">Jenerate</div>
        </div>

        <div className=" typography-jenmusic-base text-jenmusic-content-secondary">
          From prompt to song in seconds. 100% ethically trained, so you own your jenerations.
        </div>
      </div>
      <div
        style={{
          background: `url(${PlayerBgUrl})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
        }}
        className="h-[135px]"
      />
      <div className="flex gap-1 hover:opacity-80 cursor-pointer items-center" onClick={() => navigate('/')}>
        <div className=" rounded-full bg-jenmusic-interactive-accent w-6 h-6 flex justify-center items-center ">
          <RightIcon className=" fill-jenmusic-interactive" />
        </div>
        <div className="text-jenmusic-interactive typography-jenmusic-base">Start Jenerating</div>
      </div>
    </div>
  );
};

const Discover = () => {
  const cachedTracks = sessionStorage.getItem('jenMusicTracks');
  const [marketAudioListData, setMarketAudioListData] = useState();

  useEffect(() => {
    if (cachedTracks) {
      setMarketAudioListData(JSON.parse(cachedTracks));
    } else {
      fetch('https://app.jenmusic.ai/cdn/Hip-Hop%20for%20Homepage/list.json').then(async (res) => {
        const jsonData = await res.json();
        const formatTracks = (shuffleArray(flattenNestedObject(jsonData)) as any).map((trackItem: any) => {
          return {
            ...trackItem,
          };
        });
        setMarketAudioListData(formatTracks);
        sessionStorage.setItem('jenMusicTracks', JSON.stringify(formatTracks));
      });
    }
  }, []);

  return (
    <div className=" bg-jenmusic-background-primary border border-b-jenmusic-border-primary rounded-xl ">
      <div className=" p-6 lg:pl-8 lg:pt-8 lg:pb-4 text-jenmusic-content-primary typography-jenmusic-heading-medium  flex gap-3">
        <DiscoverIcon fillColor="#111112" />
        Discover
      </div>
      <div className="px-6 lg:px-8 min-h-[600px] lg:min-h-[800px]">
        <TrackList
          label={{ title: 'Title', creator: 'User', filter: 'StyleFilter' }}
          itemsPerPage={25}
          trackListData={marketAudioListData}
          // style={{ contentHeight: 'calc(100vh - 260px)' }}
        />
      </div>
    </div>
  );
};
export default HomePage;
